import { useRegisterUser } from '@/features/users/hooks';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { useAppForm } from '@/shared/lib/form';
import { toaster } from '@/shared/ui/toaster.component';
import { normalizeEmail, validateCpf, validatePhone } from '@/shared/utils';
import {
  getRoleOptionsByUserType,
  getUserGroup,
  UserRole,
} from '@/shared/utils/get-group';
import { getRoleLabel } from '@/shared/utils/get-role-label';
import { removeFormatPhone } from '@/shared/utils/remove-format-phone';
import { removeFormatRegisterNumber } from '@/shared/utils/remove-format-register-number';
import {
  Button,
  Dialog,
  Field,
  RadioCard,
  Select,
} from '@noorden-insurtech/alba-design-system';
import { revalidateLogic } from '@tanstack/react-form';
import { Building, User, UserPlus, Users } from 'lucide-react';
import { z } from 'zod';
import { RegisterUserPayload } from '../../../services';
import { UserTypeRadioItem } from '../../users/components/user-type-radio-items.component';

interface CreateBrokerUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  brokerId: string;
}

const roleOptions = getRoleOptionsByUserType('broker');

const schema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  cpf: z.string().refine(v => validateCpf(v), 'CPF inválido'),
  email: z.email('E-mail inválido'),
  phone: z.string().refine(v => validatePhone(v), 'Telefone inválido'),
  role: z.string().min(1, 'Perfil é obrigatório'),
  partyId: z.string().min(1, 'Corretora é obrigatória'),
  userType: z.literal('broker'),
});

export const CreateBrokerUserModal = ({
  isOpen,
  onClose,
  onSuccess,
  brokerId,
}: CreateBrokerUserModalProps) => {
  const { mutateAsync, isPending: isRegisteringUser } = useRegisterUser();

  const { AppField, handleSubmit, reset } = useAppForm({
    defaultValues: {
      name: '',
      cpf: '',
      email: '',
      phone: '',
      role: '',
      partyId: brokerId,
      userType: 'broker',
    },
    onSubmit: async () => {},
    validationLogic: revalidateLogic(),
    validators: {
      onChange: schema,
      onSubmitAsync: async ({ value }) => {
        const { name, cpf, email, phone, role, partyId } = value as Record<
          string,
          string
        >;

        if (!name || !email || !role || !partyId) return null;

        const group = getUserGroup('broker', role as UserRole);
        const payload: RegisterUserPayload = {
          email: normalizeEmail(email),
          name,
          partyId,
          customAttributes: {
            'custom:group': group,
            'custom:contact_number': removeFormatPhone(phone) || undefined,
            'custom:register_number':
              removeFormatRegisterNumber(cpf) || undefined,
            'custom:profile': getRoleLabel(group),
            'custom:party_type': '3',
            'custom:party_id': partyId,
          },
        };

        try {
          await mutateAsync(payload);
          toaster.create({
            type: 'success',
            title: 'Usuário adicionado',
            description: 'Usuário adicionado ao corretor com sucesso.',
          });
          reset();
          onSuccess?.();
          return null;
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Erro ao criar usuário';
          const msg = errorMessage.trim();
          const lower = msg.toLowerCase();

          toaster.create({
            type: 'error',
            title: 'Erro ao adicionar usuário',
            description: msg,
          });

          const fields: Record<string, { message: string } | undefined> = {};

          if (lower.includes('cpf')) {
            fields['cpf'] = { message: msg };
          }

          if (
            lower.includes('email') ||
            lower.includes('already exists') ||
            lower.includes('já cadastrado')
          ) {
            fields['email'] = {
              message: lower.includes('email')
                ? msg
                : 'Este e-mail já está cadastrado em outro usuário.',
            };
          }

          return { fields } as const;
        }
      },
    },
  });

  return (
    <Dialog.Root open={isOpen} onOpenChange={() => onClose()}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-[calc(100vw-1.5rem)] max-w-3xl max-h-[calc(100vh-1.5rem)] overflow-hidden'>
          <Dialog.Header>
            <Dialog.Title>
              <div className='flex items-center gap-3'>
                <UserPlus className='h-6 w-6' />
                Adicionar Usuário ao Corretor
              </div>
            </Dialog.Title>
            <Dialog.CloseButton onClick={() => onClose()} />
          </Dialog.Header>
          <Dialog.Body className='overflow-y-auto px-4 sm:px-6'>
            <div className='flex flex-col gap-6'>
              <div className='flex flex-col gap-3'>
                <RadioCard.Root
                  value='broker'
                  disabled
                  className='pointer-events-none opacity-90'
                >
                  <RadioCard.Label className='text-alba-black-500'>
                    Tipo de Usuário *
                  </RadioCard.Label>
                  <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
                    <UserTypeRadioItem
                      value='insurer'
                      icon={Building}
                      label='Seguradora'
                      description='Usuários internos da seguradora'
                    />
                    <UserTypeRadioItem
                      value='advisory'
                      icon={Users}
                      label='Assessoria'
                      description='Usuários de assessoria parceira'
                    />
                    <UserTypeRadioItem
                      value='broker'
                      icon={User}
                      label='Corretor'
                      description='Usuários de corretor parceiro'
                    />
                  </div>
                </RadioCard.Root>
              </div>

              <form
                onSubmit={async e => {
                  e.preventDefault();
                  e.stopPropagation();
                  await handleSubmit();
                }}
                className='grid grid-cols-1 gap-4 md:grid-cols-2'
              >
                <div className='md:col-span-2'>
                  <AppField name='partyId'>
                    {field => (
                      <field.AdvisoryComboboxField
                        label='Corretora Vinculada *'
                        required
                        placeholder='Corretora selecionada'
                        disabled
                        partyType='broker'
                      />
                    )}
                  </AppField>
                </div>

                <div className='md:col-span-2'>
                  <AppField name='name'>
                    {field => (
                      <field.InputField
                        label='Nome *'
                        helperText=''
                        type='text'
                        placeholder='Digite o nome do usuário'
                        required
                        maxLength={FIELD_MAX_LENGTHS.personName}
                        disabled={isRegisteringUser}
                      />
                    )}
                  </AppField>
                </div>

                <div className='md:col-span-2'>
                  <AppField name='cpf'>
                    {field => (
                      <field.InputField
                        label='CPF *'
                        required
                        helperText=''
                        type='text'
                        placeholder='XXX.XXX.XXX-XX'
                        maxLength={FIELD_MAX_LENGTHS.cpf}
                        mask='cpf'
                        disabled={isRegisteringUser}
                      />
                    )}
                  </AppField>
                </div>

                <div className='md:col-span-2'>
                  <AppField name='email'>
                    {field => (
                      <field.InputField
                        label='E-mail *'
                        required
                        helperText=''
                        type='email'
                        lowercase
                        placeholder='Digite o e-mail do usuário'
                        maxLength={FIELD_MAX_LENGTHS.email}
                        disabled={isRegisteringUser}
                      />
                    )}
                  </AppField>
                </div>

                <div className='grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-2'>
                  <AppField
                    name='role'
                    children={field => {
                      const showError =
                        !field.state.meta.isValid &&
                        (field.state.meta.isTouched ||
                          field.form.state.isSubmitted);

                      const errorText = field.state.meta.errors
                        ?.map(err => {
                          if (!err) return '';
                          if (typeof err === 'string') return err;
                          if (
                            typeof err === 'object' &&
                            'message' in err &&
                            typeof (err as { message?: unknown }).message ===
                              'string'
                          ) {
                            return (err as { message: string }).message;
                          }
                          return '';
                        })
                        .filter(Boolean)
                        .join(', ');

                      return (
                        <Field.Root required invalid={showError}>
                          <Field.Label className='text-alba-black-500'>
                            Perfil*
                          </Field.Label>
                          <Select.Root
                            items={roleOptions}
                            value={field.state.value ? [field.state.value] : []}
                            onValueChange={details =>
                              field.handleChange(details.value[0] || '')
                            }
                            onFocusOutside={field.handleBlur}
                            positioning={{ sameWidth: true }}
                            disabled={isRegisteringUser}
                          >
                            <Select.Control>
                              <Select.Trigger>
                                <Select.ValueText placeholder='Selecione o perfil' />
                                <Select.Indicator>
                                  <svg
                                    className='h-4 w-4'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                  >
                                    <path
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                      strokeWidth={2}
                                      d='M19 9l-7 7-7-7'
                                    />
                                  </svg>
                                </Select.Indicator>
                              </Select.Trigger>
                            </Select.Control>
                            <Select.Portal>
                              <Select.Positioner>
                                <Select.Content>
                                  {roleOptions.map(option => (
                                    <Select.Item
                                      key={option.value}
                                      item={option}
                                    >
                                      <Select.ItemText>
                                        {option.label}
                                      </Select.ItemText>
                                      <Select.ItemIndicator>
                                        <svg
                                          className='h-4 w-4'
                                          viewBox='0 0 20 20'
                                          fill='currentColor'
                                        >
                                          <path
                                            fillRule='evenodd'
                                            d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                                            clipRule='evenodd'
                                          />
                                        </svg>
                                      </Select.ItemIndicator>
                                    </Select.Item>
                                  ))}
                                </Select.Content>
                              </Select.Positioner>
                            </Select.Portal>
                          </Select.Root>
                          {showError && !!errorText && (
                            <Field.ErrorText>{errorText}</Field.ErrorText>
                          )}
                        </Field.Root>
                      );
                    }}
                  />

                  <AppField
                    name='phone'
                    children={field => (
                      <field.InputField
                        label='Telefone'
                        helperText=''
                        type='tel'
                        mask='(99) 9{8,9}'
                        placeholder='(XX) XXXXXXXXX'
                        maxLength={FIELD_MAX_LENGTHS.phone}
                        required={false}
                        disabled={isRegisteringUser}
                      />
                    )}
                  />
                </div>
              </form>
            </div>
          </Dialog.Body>
          <Dialog.Footer className='flex flex-col-reverse gap-2 px-4 pb-4 sm:flex-row sm:justify-end sm:px-6'>
            <Button
              variant='ghost'
              className='w-full sm:w-auto'
              onClick={() => {
                reset();
                onClose();
              }}
            >
              Cancelar
            </Button>
            <Button
              loadingText='Salvando...'
              loading={isRegisteringUser}
              variant='primary'
              className='w-full sm:w-auto'
              onClick={async () => {
                await handleSubmit();
              }}
            >
              Salvar
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
